/**
 * Formula evaluation — `SheetLispEnvironment` (exported by `@tento/lona`).
 *
 * Provides the Lisp evaluation environment for formula rows. The environment
 * resolves row references via `SheetRow.Joined.Handle` (which has RowCellCache
 * for cached cell data) and exposes date/style builtins via
 * `ModelLispLibrary`.
 *
 * `ModelLispLibrary` is internal to `@tento/lona/sheets`: it lives in
 * `sheets/src/formula/model-formula.ts`, is on no barrel, and this module imports it by
 * module path. It is structurally typed and accepts any context shape with
 * `{style, tz, column, dbg}` fields. It is not part of `@tento/lona`'s
 * public surface.
 *
 * A missing input is no value, as on the rows side (`evaluateFormula` in
 * `sheets/src/formula/model-formula.ts`): a row reference with no value on the
 * column's day reads `null` (`rowDataNow`), and `Formula.run` then answers no
 * value for the whole formula unless it tests for absence itself
 * (`formulaTestsAbsence`).
 */

import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { Lisp } from "@tento-lisp/lisp";
import { ControlFlow } from "@tento-lisp/ops/control-flow";
import { StandardLibrary } from "@tento-lisp/ops/stdlib";
import { NaiveDate } from "@tento-chrono";
import type { TimezoneRegion } from "@tento-chrono";
import { cellValueFromLisp, formulaTestsAbsence, ModelLispLibrary } from "@tento-lona/sheets/internal-sdk";
import { FormulaEngine } from "@tento-lona/sheets/platform";
import type { FormulaSheetReads } from "@tento-lona/sheets";
import { RowId, RowNetworkKey } from "@tento-lona/sheets";
import type { SheetHandle } from "../client/sheet-handle.ts";
import type { CellEntry, CellStyle, CellValue } from "@tento-lona/sheets";
import type { SheetRow } from "../rows/sheet-row.ts";
import { type SheetRowTree, treeCellAccessorOf } from "../rows/sheet-tree.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

export { ModelLispLibrary };

/**
 * What a formula evaluation reads: the sheet's row tree, its reads, the row
 * the formula belongs to and the column it computes.
 */
export type FormulaContext = {
  tree: SheetRowTree;
  sheet: FormulaSheetReads;
  row: SheetRow.Joined.Handle;
  column: NaiveDate.Partial;
  style: CellStyle;
  tz: Option<TimezoneRegion>;
  /** Debug hook read by the `render.*` formula helpers (`offset`). */
  dbg: Option<{ readonly offset: Option<number> }>;
};

const FORMULA_ROW_REGEX =
  /^\$([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// ── SheetLispEnvironment ────────────────────────────────────────────────────

/** `SheetLispEnvironment`'s friend record (`Internals`): its constructor registers each instance. */
type SheetLispEnvironmentInternals = {
  readonly readMissingInput: (env: SheetLispEnvironment) => boolean;
};
const environmentInternals = new Internals<SheetLispEnvironment, SheetLispEnvironmentInternals>("SheetLispEnvironment");

/**
 * The Lisp environment a formula cell evaluates in: its local scope over the
 * sheet's row tree, the row and the column being computed.
 */
export class SheetLispEnvironment implements Lisp.Environment {
  readonly #context: FormulaContext;
  readonly #scope: Map<string, Lisp.Expression>;
  /**
   * Shared by an environment and every clone of it (one evaluation): a row
   * reference found no value on the column's day (`Formula.run`).
   */
  #reads: { missing: boolean } = { missing: false };

  static readonly #internals: SheetLispEnvironmentInternals = {
    readMissingInput: (env) => env.#reads.missing,
  };

  constructor(
    scope: Map<string, Lisp.Expression>,
    context: FormulaContext,
    public readonly evalCount: Lisp.EvalCount = { n: 0 },
  ) {
    environmentInternals.register(this, SheetLispEnvironment.#internals);
    this.#scope = scope;
    this.#context = context;
  }

  clone(keys: string[], values: Lisp.Expression[]): Lisp.Environment {
    const cloned = new Map(this.#scope);
    keys.forEach((key, index) => {
      const value = values[index];
      if (value !== undefined) cloned.set(key, value);
    });
    const env = new SheetLispEnvironment(cloned, this.#context, this.evalCount);
    env.#reads = this.#reads;
    return env;
  }

  set(key: string, value: Lisp.Expression): void {
    this.#scope.set(key, value);
  }

  get(key: string): Option<Lisp.Expression> {
    if (this.#scope.has(key)) {
      return this.#scope.get(key) ?? null;
    }

    const rowNameMatch = key.match(/^rows\["([^"]+)"\]$/);
    if (rowNameMatch) {
      const nameOrKey = rowNameMatch[1];
      const foundRow = this.#context.tree.formulaRow(nameOrKey ?? "");
      if (!foundRow) {
        throw new Error(`Row not found: "${nameOrKey}"`);
      }
      return this.rowDataNow(foundRow);
    }

    const rowIdMatch = key.match(FORMULA_ROW_REGEX);
    if (rowIdMatch) {
      const hex = (rowIdMatch[1] ?? "").replace(/-/g, "");
      let foundById: Option<SheetRow.Joined.Handle> = null;
      for (const [id, r] of this.#context.tree.byId) {
        if (id.toString().includes(hex)) { foundById = r; break; }
      }
      if (!foundById) {
        throw new Error(`Row not found with ID: "${rowIdMatch[1]}"`);
      }
      return this.rowDataNow(foundById);
    }

    if (ModelLispLibrary.has(key)) {
      const contextFn = nonnull(
        ModelLispLibrary.get(key),
        `ModelLispLibrary: "${key}" registered`,
      );
      return contextFn(this.#context);
    }
    if (ControlFlow.has(key)) {
      return ControlFlow.get(key) ?? null;
    }
    if (StandardLibrary.has(key)) {
      return StandardLibrary.get(key) ?? null;
    }
    return key;
  }

  /**
   * `row`'s value on the column's day: its cell there, or a cell of another
   * scale containing the day. `null` — no value — when the row has no such
   * cell, or one holding no value; the evaluation is then marked, and
   * `Formula.run` answers no value for the formula (a missing input is not
   * zero).
   */
  rowDataNow(row: SheetRow.Joined.Handle): Option<Lisp.Atom> {
    return this.#input(this.#rowValue(row));
  }

  #rowValue(row: SheetRow.Joined.Handle): Option<Lisp.Atom> {
    const rh = treeCellAccessorOf(this.#context.tree)?.peekRowHandle(
      RowNetworkKey.of(row.id),
    ) ?? row.joined.rowHandle;
    if (!rh) return null;

    const exact = rh.getAllInRange(this.#context.column.toRange());
    const first = exact[0];
    if (first !== undefined) return cellValueToAtom(row, first.value);

    for (const cell of rh.allCells()) {
      if (cell.kind !== "date") continue;
      if (cell.date.type === this.#context.column.type) continue;
      if (cell.date.toRange().contains(this.#context.column.start)) {
        return cellValueToAtom(row, cell.value);
      }
    }
    return null;
  }

  /** A row reference's value; `null` (no value that day) marks the evaluation. */
  #input(value: Option<Lisp.Atom>): Option<Lisp.Atom> {
    if (value === null) this.#reads.missing = true;
    return value;
  }
}

function cellValueToAtom(
  row: SheetRow.Joined.Handle,
  data: CellValue,
): Option<Lisp.Atom> {
  if (data == null) return null;
  if (row.descriptor.id === "number" && typeof data === "string") {
    return normalizeFormulaAtom(Number.parseFloat(data));
  }
  return normalizeFormulaAtom(data as Lisp.Atom);
}

/** A `rows["Label"]` symbol: the label or key it reads. */
const ROW_REFERENCE = /^rows\["([^"]+)"\]$/;

/**
 * A short-format reference inside a symbol: `~` then `ident_or_key` (the short sheet format's grammar: idents joined by
 * `:`, then `@`-qualified dotted names); a `.` after it starts a field path, which is not part of the key.
 */
const SHORT_REFERENCE = /(?<![A-Za-z0-9_-])~([A-Za-z_][A-Za-z0-9_-]*(?::[A-Za-z_][A-Za-z0-9_-]*)*(?:@[A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*)*)/g;

function normalizeFormulaAtom(value: Lisp.Atom): Lisp.Atom {
  return typeof value === "number" && Object.is(value, -0) ? 0 : value;
}

// ── Formula namespace ───────────────────────────────────────────────────────

/**
 * Formula evaluation on the SDK side: builds the sheet reads a formula's
 * `cells` builtin reads.
 */
export namespace Formula {
  /**
   * Build a `FormulaSheetReads` adapter over lona-js's `SheetRowTree` + cell
   * accessor. The cross-package `ModelLispLibrary.cells` builtin reads
   * this shape; all other lisp row lookups (`rows["..."]`) are handled
   * by `SheetLispEnvironment.get` directly off `context.tree`.
   */
  export function makeSheetReads(
    tree: SheetRowTree,
    cells: Formula.CellSource,
  ): FormulaSheetReads {
    return {
      peekCellsByLabelOrKey(labelOrKey) {
        const row = tree.formulaRow(labelOrKey);
        return row == null ? null : cells.peekAll(row.id);
      },
    };
  }

  /**
   * A name a formula expression reads an input by: as written (`~whoop-sleep`, `Sales`), and the name a sheet
   * resolves (`key`: a lookup key for a `~` reference, which a sheet stores with or without its `:` / `~` marker; a
   * label or key for `rows["…"]` and `(cells "…")`).
   */
  export interface Reference {
    /** The reference as the expression writes it: `~whoop-sleep`, `Sales`. */
    readonly written: string;
    /** The name a sheet resolves it by: `whoop-sleep` (no marker, no field path), `Sales`. */
    readonly key: string;
    /** A `~key` reference (the short format's `ref`), read as a lookup key in any of its spellings. */
    readonly lookup: boolean;
  }

  /**
   * The inputs `expression` names, in order, as the formula language reads it (`Lisp.parse`): `rows["Label"]`,
   * `(cells "Label")`, and the short sheet format's `~key` references (its grammar's `ref ::= "~" ident_or_key`),
   * several of which may share one symbol (`~a+~b`). A reference's key ends where its field path
   * begins (`~whoop-sleep.score.sleep_performance_percentage` reads `whoop-sleep`); a `~key` followed by `{` is a
   * template filled in later, never a reference yet. Comments and string literals name nothing; an expression the
   * reader refuses names nothing it can vouch for (`[]`).
   */
  export function references(expression: string): readonly Reference[] {
    let ast: Lisp.Expression;
    try {
      ast = Lisp.parse(expression);
    } catch {
      return [];
    }
    const out: Reference[] = [];
    const literal = (exp: unknown): Option<string> =>
      exp != null && typeof exp === "object" && !Array.isArray(exp) && typeof Reflect.get(exp, "value") === "string"
        ? Reflect.get(exp, "value")
        : null;
    const visit = (exp: unknown): void => {
      if (typeof exp === "string") {
        const row = ROW_REFERENCE.exec(exp)?.[1];
        if (row != null) {
          out.push({ written: row, key: row, lookup: false });
          return;
        }
        for (const match of exp.matchAll(SHORT_REFERENCE)) {
          const key = match[1] ?? "";
          const after = exp.slice((match.index ?? 0) + match[0].length);
          if (key === "" || after.startsWith("{") || after.startsWith(":{")) continue;
          out.push({ written: `~${key}`, key, lookup: true });
        }
        return;
      }
      const expr: unknown = exp != null && typeof exp === "object" && !Array.isArray(exp) ? Reflect.get(exp, "expr") : null;
      const list: Option<readonly unknown[]> = Array.isArray(exp) ? exp : Array.isArray(expr) ? expr : null;
      if (list == null) return;
      const label = list[0] === "cells" ? literal(list[1]) : null;
      if (label != null) out.push({ written: label, key: label, lookup: false });
      for (let i = 0; i < list.length; i++) {
        // `rows["Label"]`: the reader splits it into the symbol `rows` and the vector `["Label"]`.
        const row = list[i] === "rows" ? rowsIndex(list[i + 1]) : null;
        if (row != null) {
          out.push({ written: row, key: row, lookup: false });
          i++;
          continue;
        }
        visit(list[i]);
      }
    };
    /** The label a `rows[…]` index names: a vector of one string literal (`["vector", {value: "Label"}]`). */
    const rowsIndex = (exp: unknown): Option<string> =>
      Array.isArray(exp) && exp.length === 2 && exp[0] === "vector" ? literal(exp[1]) : null;
    visit(ast);
    return out;
  }

  /** The cell read a formula view needs: a row's cached date-keyed cells
   *  (`platformOf(handle)` is one). */
  export type CellSource = Pick<SheetHandle.Platform, "peekAll">;

  export function extractDependencies(
    formula: string,
    rows: { id: { toString(): string }; label: Option<string>; lookupKey: Option<string> }[],
  ): RowId[] {
    const ast = Lisp.parse(formula);
    const names: string[] = [];
    const ids: string[] = [];
    const ROW_NAME_REGEX = /^rows\["([^"]+)"\]$/;

    function visit(exp: Lisp.Expression): void {
      if (typeof exp === "string") {
        const rowIdMatch = exp.match(FORMULA_ROW_REGEX);
        const rowId = rowIdMatch?.[1];
        if (rowId !== undefined) {
          ids.push(rowId);
          return;
        }
        const rowNameMatch = exp.match(ROW_NAME_REGEX);
        const rowName = rowNameMatch?.[1];
        if (rowName !== undefined) {
          names.push(rowName);
          return;
        }
        return;
      }

      if (
        exp &&
        typeof exp === "object" &&
        "expr" in exp &&
        Array.isArray((exp as Lisp.OptimizedExpression).expr)
      ) {
        for (const subexp of (exp as Lisp.OptimizedExpression).expr) {
          visit(subexp);
        }
        return;
      }

      if (Array.isArray(exp)) {
        for (const subexp of exp) {
          visit(subexp);
        }
      }
    }

    visit(ast);

    const result: RowId[] = [];
    for (const name of names) {
      const found = rows.find((r) => r.lookupKey === name || r.label === name);
      if (found) result.push(RowId.of(found.id.toString()));
    }
    for (const id of ids) {
      result.push(RowId.of(id));
    }
    return result;
  }

  /**
   * Run `expression` — the parse of `formula` — in `env` as a formula cell
   * computes it: its value, or `null` (no value) when it read a row with no
   * value on the column's day and does not test for absence itself
   * (`null?` / `empty?`), also when the arithmetic then throws on the
   * missing input. `(count (cells …))` reads a list, whose empty day is a
   * value; a formula that references no row has a value on every day.
   * Otherwise as `Lisp.run`: a throw answers its text, a cache miss `null`.
   */
  export function run(
    expression: Lisp.Expression,
    formula: string,
    env: SheetLispEnvironment,
  ): Option<Lisp.Expression> {
    const handlesAbsence = formulaTestsAbsence(formula);
    try {
      const result = Lisp.evalThrows(expression, env);
      return environmentInternals.of(env).readMissingInput(env) && !handlesAbsence ? null : result ?? null;
    } catch (e) {
      if (environmentInternals.of(env).readMissingInput(env) && !handlesAbsence) return null;
      if (e instanceof Lisp.CacheMissError || e instanceof FormulaEngine.AmbiguousReference) return null;
      return String(e);
    }
  }

  export function makeComputeRange(
    formula: string,
    currentRow: SheetRow.Joined.Handle,
    tree: SheetRowTree,
    cells: Formula.CellSource,
  ): (range: NaiveDate.Range) => Promise<CellEntry[]> {
    const ast = Lisp.parse(formula);
    const sheet = makeSheetReads(tree, cells);

    return async (range: NaiveDate.Range) => {
      const entries: CellEntry[] = [];
      for (const date of range.iter()) {
        const partial = new NaiveDate.Partial(
          "day",
          date.yr,
          date.month1,
          date.day1,
        );
        const style: CellStyle = new Map();
        const env = new SheetLispEnvironment(new Map(), {
          tree,
          sheet,
          row: currentRow,
          column: partial,
          style,
          tz: null,
          dbg: null,
        });
        const result = Formula.run(ast, formula, env);
        // `Formula.run` yields an expression (an atom, a list, a procedure);
        // `cellValueFromLisp` keeps what a cell can hold (`-0` as `0`) and
        // drops a procedure.
        const value = result == null ? null : cellValueFromLisp(result);
        if (value != null) entries.push({ date: partial, id: null, value, style });
      }
      return entries;
    };
  }
}
